import React, { Component, HTMLAttributes } from "react";
import { InputError } from "../InputError/InputError";
import thousands from "thousands";
import { InputInfo } from "../InputInfo/inputInfo";
import {
  FormFieldProps,
  FormFieldString,
} from "../../commonTypes/formField.types";
import { hashCode } from "../../utils/hash";

export interface TextInputProps extends FormFieldProps<string> {
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
  max?: number;
  min?: number;
  type?: string;
  multiline?: boolean;
  changeOnBlur?: boolean;
  classNames?: string;
  additionalText?: string;
  actAsNumber?: boolean;
  labelClass?: string;
  autoComplete?: string;
  ariaLabelHidden?: boolean;
  showMaxLengthMessage?: boolean;
  disablePaste?: boolean;
  /**
   * Default: 'sv'. Supports 'sv', 'en'
   */
  maxLengthMessageLanguage?: string;

  isWrappedByControlledElement?: boolean;
}

const DEFAULT_STATE_VALUE = "";
export class TextInput extends Component<TextInputProps, FormFieldString> {
  static defaultProps = {
    fieldName: "",
    value: "",
    isRequired: false,
    changeOnBlur: true,
    errorMessage: "",
    showMaxLengthMessage: false,
    type: "text",
    classNames: "",
    disabled: false,
    readonly: false,
    actAsNumber: false,
    //min: 0,
    isTouched: false,
    hidden: false,
    isWrappedByControlledElement: false,
    referenceValue: null,
    maxLengthMessageLanguage: "sv",
    disablePaste: false,
    onChange: () => {
      /* noop */
    },
    onBlur: () => {},
  } as TextInputProps;

  private _id: string;
  private _errorId: string;
  private _latestValue: string | undefined;

  constructor(props: TextInputProps) {
    super(props);
    this._id = props.id || hashCode(props.fieldName, "textInput");
    this._errorId = hashCode(props.id || props.fieldName, "error");
    const { fieldName, value, isRequired, label, isTouched, referenceValue } =
      props;

    let showMaxLengthMessage = false;
    if (props.value !== undefined) {
      this._latestValue = value;
      if (props.value !== null)
        showMaxLengthMessage = props.value.length === props.maxLength;
    } else {
      this._latestValue = DEFAULT_STATE_VALUE;
    }

    this.state = {
      value: this._latestValue,
      fieldName,
      isRequired,
      label,
      isTouched,
      errorMessage: "",
      showMaxLengthMessage,
      referenceValue,
    };
  }

  componentDidUpdate = (prevProps: TextInputProps) => {
    const { referenceValue } = this.props;
    if (prevProps.referenceValue !== referenceValue) {
      this.setState({
        referenceValue,
      });
    }
  };

  public render() {
    const {
      multiline,
      label,
      additionalText,
      maxLength,
      labelClass,
      ariaLabelHidden,
      referenceValue,
    } = this.props;

    const errorMessage = this._errorMessage;
    const validityClass = errorMessage ? "has-error" : "";
    return (
      <div
        className={`sos-textinput ${validityClass}`}
        data-referencevalue={referenceValue}
      >
        {label && (
          <label
            htmlFor={this._id}
            aria-hidden={ariaLabelHidden}
            className={labelClass}
          >
            {label}
          </label>
        )}
        {additionalText && (
          <div className="form-group-xxs-small">{additionalText}</div>
        )}
        {multiline ? this.renderTextArea() : this.renderTextInput()}
        {this.state.showMaxLengthMessage && this.getMaxLengthInfoByLanguage()}
        {errorMessage && (
          <InputError
            delayRender={0}
            id={this._errorId}
            errorText={errorMessage}
          />
        )}
      </div>
    );
  }

  getMaxLengthInfoByLanguage = (): JSX.Element => {
    const { maxLengthMessageLanguage } = this.props;

    let text: string;
    switch (maxLengthMessageLanguage) {
      case "sv":
        text = this._maxLengthMsgSv;
        break;
      case "en":
        text = this._maxLengthMsgEn;
        break;
      default:
        text = this._maxLengthMsgSv;
        break;
    }

    return <InputInfo infoText={text} />;
  };

  private get _maxLengthMsgSv(): string {
    return `Du kan ange max ${
      this.props.actAsNumber
        ? this.state.value.replace(/\D/g, "").length + " siffror"
        : this.props.maxLength + " tecken"
    }`;
  }

  private get _maxLengthMsgEn(): string {
    return `You are allowed a maximum of ${
      this.props.actAsNumber
        ? this.state.value.replace(/\D/g, "").length + " digits"
        : this.props.maxLength + " characters"
    }`;
  }

  private get _errorMessage(): string | undefined {
    let { errorMessage } = this.state;
    if (!errorMessage && this.props.errorMessage) {
      errorMessage = this.props.errorMessage;
    }
    return errorMessage;
  }

  private get _ariaAttributes(): HTMLAttributes<any> {
    const { errorAriaAttributes, errorMessage } = this.props;
    if (errorAriaAttributes) return errorAriaAttributes;
    if (errorMessage)
      return { "aria-invalid": true, "aria-describedby": this._errorId };
    return {};
  }

  private renderTextInput(): JSX.Element {
    const {
      minLength,
      maxLength,
      pattern,
      type,
      fieldName,
      placeholder,
      isRequired,
      classNames,
      max,
      min,
      disabled,
      readonly,
      hidden,
      autoComplete,
      isWrappedByControlledElement,
    } = this.props;
    const formValue =
      (isWrappedByControlledElement ? this.props.value : this.state.value) ||
      "";

    return (
      <input
        type={type}
        id={this._id}
        name={fieldName}
        value={formValue}
        onChange={this.onChangeValue}
        onBlur={this.onBlur}
        onFocus={this.onFocus}
        onPaste={this.onPaste}
        placeholder={placeholder}
        minLength={minLength}
        maxLength={maxLength}
        max={max}
        min={min}
        required={isRequired}
        disabled={disabled}
        readOnly={readonly}
        hidden={hidden}
        className={`form-control ${classNames}`}
        autoComplete={autoComplete}
        {...this._ariaAttributes}
      />
    );
  }

  private renderTextArea(): JSX.Element {
    const {
      minLength,
      maxLength,
      pattern,
      fieldName,
      placeholder,
      isRequired,
      classNames,
      disabled,
      readonly,
      autoComplete,
      isWrappedByControlledElement,
    } = this.props;
    const formValue =
      (isWrappedByControlledElement ? this.props.value : this.state.value) ||
      "";

    return (
      <textarea
        id={this._id}
        name={fieldName}
        value={formValue}
        onChange={this.onChangeValue}
        placeholder={placeholder}
        onBlur={this.onBlur}
        onFocus={this.onFocus}
        onPaste={this.onPaste}
        required={isRequired}
        minLength={minLength}
        maxLength={maxLength}
        disabled={disabled}
        readOnly={readonly}
        autoComplete={autoComplete}
        className={`form-control ${classNames}`}
        {...this._ariaAttributes}
      />
    );
  }

  private onBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { pattern, patternMessage, referenceValue } = this.props;

    //FOET-374: Remove NUL and other control characters from text inputs
    //https://en.wikipedia.org/wiki/Control_character#In_Unicode
    const value = e.target.value?.replace("\u0000", "");

    const updatedValues: FormFieldString = {
      ...this.state,
      value,
      isTouched: true,
      pattern,
      patternMessage,
      referenceValue,
    };

    if (this.props.changeOnBlur) {
      this.props.onChange(updatedValues);
    }
    this.props.onBlur(e);

    this.setState(updatedValues);
  };

  private onFocus = (_: React.FocusEvent) => {
    if (this.props.actAsNumber && this.state.value === "0") {
      let input = document.getElementById(this._id) as HTMLInputElement;
      if (input) input.setSelectionRange(0, this.state.value.length);
    }
  };
  private onPaste = (e: any) => {
    if (this.props.disablePaste) e.preventDefault();
  };

  private onChangeValue = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let value = e.target.value;

    if (value === this._latestValue) {
      return;
    }

    if (this.props.actAsNumber) {
      value = this.formatInput(value);
    }

    this._latestValue = value;
    let showMaxLengthMessage = false;
    if (value.length === this.props.maxLength) {
      showMaxLengthMessage = true;
    }

    const updatedValues: FormFieldString = {
      ...this.state,
      value,
      isTouched: true,
      showMaxLengthMessage,
      referenceValue: this.props.referenceValue, // this should always come from props
    };

    if (!this.props.changeOnBlur) {
      this.props.onChange(updatedValues);
    }

    this.setState(updatedValues);
  };

  private formatInput = (input: string): string => {
    return thousands(parseInt(input.replace(/\D/g, ""), 10), " ");
  };
}
